import { supabase } from '../lib/supabase';

const PLACEHOLDER_URL = 'https://via.placeholder.com/150';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const IMAGE_TIMEOUT = 10000;

interface FetchOptions {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
}

export const imageService = {
  async fetchWithTimeout(url: string, options: RequestInit & FetchOptions = {}): Promise<Response> {
    const { timeout = IMAGE_TIMEOUT, retries = MAX_RETRIES, retryDelay = RETRY_DELAY, ...fetchOptions } = options;
    
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        const response = await fetch(url, {
          ...fetchOptions,
          signal: controller.signal,
          mode: 'cors',
          credentials: 'omit',
          headers: {
            ...fetchOptions.headers,
            'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
            'Pragma': 'no-cache'
          }
        }).finally(() => clearTimeout(timeoutId));

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        return response;
      } catch (error) {
        lastError = error as Error;
        if (attempt < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }
    
    throw lastError || new Error('Failed to fetch after retries');
  },

  async convertToJpeg(blob: Blob): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 400; // Fixed width for avatars
        canvas.height = 400; // Fixed height for avatars
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Fill white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Calculate dimensions to maintain aspect ratio
        const scale = Math.min(
          canvas.width / img.width,
          canvas.height / img.height
        );
        const x = (canvas.width - img.width * scale) / 2;
        const y = (canvas.height - img.height * scale) / 2;

        // Draw image
        ctx.drawImage(
          img,
          x,
          y,
          img.width * scale,
          img.height * scale
        );

        // Convert to JPEG blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to convert image to JPEG'));
            }
          },
          'image/jpeg',
          0.9
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.crossOrigin = 'anonymous';
      img.src = URL.createObjectURL(blob);
    });
  },

  async getPlaceholderUrl(): Promise<string> {
    try {
      // Check if placeholder already exists in storage
      const { data: existingFiles } = await supabase.storage
        .from('avatars')
        .list('', { 
          search: 'placeholder.jpeg'
        });

      if (existingFiles && existingFiles.length > 0) {
        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl('placeholder.jpeg');
        return publicUrl;
      }

      // Create placeholder image
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 400;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');

      // Fill with light gray
      ctx.fillStyle = '#E5E7EB';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Add text
      ctx.fillStyle = '#6B7280';
      ctx.font = '24px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No Image', canvas.width / 2, canvas.height / 2);

      // Convert to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create placeholder blob'));
          },
          'image/jpeg',
          0.9
        );
      });

      // Upload to storage
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload('placeholder.jpeg', blob, {
          cacheControl: '31536000',
          upsert: true,
          contentType: 'image/jpeg'
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(data.path);

      return publicUrl;
    } catch (error) {
      console.error('Error creating placeholder:', error);
      return PLACEHOLDER_URL;
    }
  },

  async uploadAvatar(imageUrl: string, username: string): Promise<string> {
    try {
      if (!imageUrl || imageUrl === PLACEHOLDER_URL) {
        return await this.getPlaceholderUrl();
      }

      // Handle data URLs
      if (imageUrl.startsWith('data:')) {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const jpegBlob = await this.convertToJpeg(blob);
          
          const safeUsername = username.replace(/[^a-zA-Z0-9]/g, '');
          const filename = `${safeUsername}-${Date.now()}.jpeg`;

          const { data, error } = await supabase.storage
            .from('avatars')
            .upload(filename, jpegBlob, {
              cacheControl: '0',
              upsert: true,
              contentType: 'image/jpeg'
            });

          if (error) {
            console.error('Storage upload error:', error);
            return await this.getPlaceholderUrl();
          }

          const { data: { publicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(data.path);

          // Update the storage_avatar_url in the database
          const { error: updateError } = await supabase
            .from('instagram_accounts')
            .update({ 
              storage_avatar_url: data.path,
              avatar: publicUrl 
            })
            .eq('username', username);

          if (updateError) {
            console.error('Error updating storage_avatar_url:', updateError);
          }

          return publicUrl;
        } catch (error) {
          console.error('Error processing data URL:', error);
          return await this.getPlaceholderUrl();
        }
      }

      // If URL is already a Supabase URL, verify it exists
      if (imageUrl.includes(import.meta.env.VITE_SUPABASE_URL)) {
        try {
          const response = await this.fetchWithTimeout(imageUrl);
          const blob = await response.blob();
          if (blob.size > 0) {
            return imageUrl;
          }
        } catch (e) {
          console.error('Failed to verify existing avatar:', e);
        }
      }

      // Use proxy for external URLs
      const proxyUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/image-proxy?url=${encodeURIComponent(imageUrl)}`;
      
      const response = await this.fetchWithTimeout(proxyUrl, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Accept': 'image/*'
        }
      });

      if (!response.ok) {
        console.error('Proxy error:', response.status);
        return await this.getPlaceholderUrl();
      }

      const blob = await response.blob();
      if (!blob || blob.size === 0) {
        console.error('Invalid image data');
        return await this.getPlaceholderUrl();
      }

      // Convert any image format to JPEG
      const jpegBlob = await this.convertToJpeg(blob);
      
      const safeUsername = username.replace(/[^a-zA-Z0-9]/g, '');
      const filename = `${safeUsername}-${Date.now()}.jpeg`;

      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(filename, jpegBlob, {
          cacheControl: '0',
          upsert: true,
          contentType: 'image/jpeg'
        });

      if (error) {
        console.error('Storage upload error:', error);
        return await this.getPlaceholderUrl();
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(data.path);

      // Update the storage_avatar_url in the database
      const { error: updateError } = await supabase
        .from('instagram_accounts')
        .update({ 
          storage_avatar_url: data.path,
          avatar: publicUrl 
        })
        .eq('username', username);

      if (updateError) {
        console.error('Error updating storage_avatar_url:', updateError);
      }

      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      return await this.getPlaceholderUrl();
    }
  },

  async handleImageError(imageUrl: string, username: string): Promise<string> {
    try {
      if (!imageUrl || imageUrl === PLACEHOLDER_URL) {
        return await this.getPlaceholderUrl();
      }

      // If URL is already a Supabase URL, verify it exists
      if (imageUrl.includes(import.meta.env.VITE_SUPABASE_URL)) {
        try {
          const response = await this.fetchWithTimeout(imageUrl);
          const blob = await response.blob();
          if (blob.size > 0) {
            return imageUrl;
          }
        } catch (e) {
          console.error('Failed to verify existing avatar:', e);
        }
      }

      // Try to upload the image again
      return await this.uploadAvatar(imageUrl, username);
    } catch (error) {
      console.error('Error handling image:', error);
      return await this.getPlaceholderUrl();
    }
  }
};